import _                                              from 'underscore';
import React, { FC, useContext, useEffect, useState } from 'react';
import classNames                                     from 'classnames';
import { Link, useLocation, useRouteMatch }           from 'react-router-dom';
import { Dropdown, Icon, Menu, MenuProps, Popup }     from 'semantic-ui-react';
import { Cell, Grid }                                 from "styled-css-grid";

import { objectEquivalenceDiff, RouterUtility }                                                       from '../../../util';
import { useCurrentUser, useFeatureFlags, usePageNavs, usePrefStates, usePrevious, useProject }       from '../../../hooks';
import { CorvalProjectAccount, CorvalUIColor, Position, RouterLocationParams, SemanticUIColor, Size } from '../../../types';
import { StyledContainer }                                                                            from '../../containers';
import { GenericNav, GenericNavComponentItem, GenericNavItem }                                        from './GenericNav';
import { useSystemSettings }                                                                          from "@src/hooks/useSystemSettings";
import { ProjectContext }                                                                             from "@src/views/pages/projects/ProjectIndexPage";
import { FinePrintMessage }                                                                           from "@src/views/components/util/messaging/FinePrintMessage";
import { ActiveProjectVersionIndicator }                                                              from '@src/views/components/project/ActiveProjectVersionIndicator';
import { DefaultProjectVersionLabel }                                                                 from '@src/views/components/project/DefaultProjectVersionLabel';


interface MainNavProps extends Omit<MenuProps, 'className'> {}

const defaultProps: MainNavProps = {};

const NavComponent: FC<MainNavProps> = ( props ) => {

    let location                                           = useLocation(),
        match                                              = useRouteMatch<RouterLocationParams.ProjectSpecificPage>(),
        { currentUser }                                    = useCurrentUser(),
        { openProject }                                    = useProject(),
        { generateProjectLevelNav }                        = usePageNavs(),
        [ prefState, prefs, prefEnums ]                    = usePrefStates(),
        featureFlags                                       = useFeatureFlags(),
        prevFeatureFlags                                   = usePrevious( featureFlags ),
        { filterProjectsByVisibility }                     = useSystemSettings(),
        { setProjectVersion, projectId, projectVersionId } = useContext( ProjectContext );

    const noProjectContext = _.isNull( projectId );

    const getProjects = () => {

        const [ visibleProjects ] = filterProjectsByVisibility( currentUser.projects, 'projectId' );

        return visibleProjects as CorvalProjectAccount[];
    };

    let hasProjects = () => {

        return ( currentUser && getProjects() );
    };

    let getSelectedProjectInfo = ( id ) => {

        if( !currentUser && getProjects() ) return null;

        const selectedProject = _.findWhere( currentUser?.projects ?? [], { projectId: id } );

        return selectedProject ? selectedProject.project : null;
    };

    let getActiveProjects = () => {

        return hasProjects() ? _.filter( getProjects(), p => p.project.isActive ) : [];
    };

    let generateProjectNavObjects = ( id ) => {

        return generateProjectLevelNav( {
            projectId:       id,
            includeChildren: true,
        } );
    };

    // Are we inside a project currently? We know this is the URL has a project ID inside it...
    let lastKnownProjectId = prefs.get( prefEnums.PROJECT_ID ) ?? null,
        currentProjectId   = match.params?.projectId ?? null;


    // ensure last known project is in the users project list
    if( !lastKnownProjectId || !getSelectedProjectInfo( lastKnownProjectId ) ){
        lastKnownProjectId = null;
    }

    const [ , projectSpecificPrefs ] = usePrefStates( currentProjectId ?? lastKnownProjectId );

    // Current state of project nav selection
    let [ dropdownProjectId, setDropdownProjectId ] = useState( currentProjectId ?? lastKnownProjectId ),
        { updateRootAndSubSection }                 = useProject( currentProjectId ),
        [ projectNavObjects, setProjectNavObjects ] = useState(
            currentProjectId ? generateProjectNavObjects( currentProjectId ) :
            lastKnownProjectId ? generateProjectNavObjects( lastKnownProjectId ) :
            null
        );

    const rawPrefVersionId = projectSpecificPrefs.get( prefEnums.PROJECT_VERSION_ID );
    const prefProjectVersionId =
        rawPrefVersionId !== undefined && rawPrefVersionId !== null && rawPrefVersionId !== ''
            ? String( rawPrefVersionId )
            : null;
    const currentProjectVersionId = projectVersionId ?? prefProjectVersionId;

    const selectedProjectForNav = dropdownProjectId ? getSelectedProjectInfo( dropdownProjectId ) : null;
    const showVersionDropdown = Boolean(
        dropdownProjectId
        && selectedProjectForNav
        && !selectedProjectForNav.isCorvalManaged
        && selectedProjectForNav.projectVersions?.length
    );

    // When feature flags change, regen menu
    useEffect( () => {

        if( prevFeatureFlags ){

            let featureFlagChanges = objectEquivalenceDiff( featureFlags, prevFeatureFlags );

            if( featureFlagChanges.diff.length > 0 ){
                setProjectNavObjects(
                    currentProjectId ? generateProjectNavObjects( currentProjectId ) :
                    lastKnownProjectId ? generateProjectNavObjects( lastKnownProjectId ) :
                    null
                );
            }
        }

    }, [ prefState, currentUser, featureFlags ] );

    /* Menu config */
    let menuClasses              = classNames( 'no-margin fade-in position-relative' ),
        menuLinkClasses          = classNames( 'opacity--80 opacity-hover--100' ),
        menuActiveLinkClasses    = classNames( '' ),
        submenuLinkClasses       = classNames( {
            'text--colored-blue':    true,
            'display--block':        true,
            'padding-xs-horizontal': true,
        } ),
        submenuActiveLinkClasses = classNames( {
            'text--colored-red':     true,
            'display--block':        true,
            'padding-xs-horizontal': true,
        } ),
        menuProps: MenuProps     = {
            inverted: true,
            text:     true,
            compact:  true,
        };

    /* Routes for platform menus */
    let routePaths = {
        home:        RouterUtility.getRoutePath( 'HOME' ) as string,
        dashboard:   RouterUtility.getRoutePath( 'DASHBOARD' ) as string,
        doraAi:      RouterUtility.getRoutePath( 'DORA_AI' ) as string,
        consultantDash: RouterUtility.getRoutePath( 'CONSULTANT' ) as string,
        projectBase: RouterUtility.getRoutePath( 'PROJECT' ) as string,
        admin:       {
            home:     RouterUtility.getRoutePath( 'ADMIN' ) as string,
            DEM:      RouterUtility.getRoutePath( 'ADMIN.DECISION_ENGINE' ) as string,
            accounts: RouterUtility.getRoutePath( 'ADMIN.SYSTEM_ACCOUNTING' ) as string,
        }
    };

    // Active path inspection
    let pathIsActive = ( path ) => {

        if( !path ) return false;

        return RouterUtility.pathContainsFragment( location.pathname, path );
    };

    let getMenuLinkClassesForPath = ( path ) => {

        return pathIsActive( path ) ? menuActiveLinkClasses : menuLinkClasses;
    };

    let getSubmenuLinkClassesForPath = ( path ) => {

        return pathIsActive( path ) ? submenuActiveLinkClasses : submenuLinkClasses;
    };

    /* On dropdown select... */
    let handleChangeProjectContext = ( projectId ) => {

        let newProjectNav          = generateProjectNavObjects( projectId ),
            navToNewProjectSection = ( dropdownProjectId != null && currentProjectId != null );

        // Save the project context
        prefs.update( prefEnums.PROJECT_ID, projectId );

        // If we navigated to a new project from an existing project...
        if( noProjectContext || navToNewProjectSection ){

            // open that new project with the same section
            openProject( projectId );

        } else {

            // otherwise set the project selection and set the new project nav objects
            setDropdownProjectId( projectId );
            setProjectNavObjects( newProjectNav );
        }
    };

    let handleChangeProjectSection = ( routePath: string, subsectionItem: GenericNavItem = null ) => {

        if( subsectionItem ){

            updateRootAndSubSection( routePath, subsectionItem.path );

        } else {

            prefs.update( prefEnums.PROJECT_SECTION, routePath );
        }
    };

    let handleChangeProjectVersion = ( versionId ) => {

        console.log( "Handling version change..." );
        console.log( { versionId } );

        projectSpecificPrefs.update( prefEnums.PROJECT_VERSION_ID, versionId );

        if( !noProjectContext ){
            setProjectVersion( versionId );
        }
    };

    return (
        <Grid columns={ `minmax(200px,1fr) auto` }
              columnGap={ '0px' }
              rowGap={ '0px' }
              areas={ [ 'project-menu' ] }>

            {/* Project Context */ }
            <Cell middle center area={ 'project-menu' }>

                <StyledContainer bgColor={ CorvalUIColor.DARKBLUE } style={ { borderRadius: 50 } } padding={ Size.XS }>

                    <Grid columns={ `1fr auto` }>

                        <Cell>

                            {/* Project Selection */ }
                            <Menu className={ menuClasses } { ...menuProps } style={ { zIndex: 1 } }>

                                { hasProjects() && (
                                    <Menu.Item className={ 'no-padding' }>

                                        <StyledContainer bgColor={ dropdownProjectId ? SemanticUIColor.BLUE : null }
                                                         borderSize={ dropdownProjectId ? Size.SM : null }
                                                         borderColor={ dropdownProjectId ? CorvalUIColor.LIGHTBLUE : null }
                                                         className={ 'top-nav-project-select padding-xs padding-sm-left margin-xs-left' }
                                                         style={ { borderRadius: 50 } }>

                                            <Dropdown item
                                                      scrolling={ true }
                                                      className={ classNames( dropdownProjectId ? menuLinkClasses : [], 'flexbox--justify-between' ) }
                                                      style={ { maxWidth: 200 } }
                                                      trigger={ (
                                                          <div className="text text--no-wrap text-capitalize" role="alert" aria-live="polite" aria-atomic="true">
                                                              { getSelectedProjectInfo( dropdownProjectId )?.name ?? 'Select Project' }
                                                          </div>
                                                      ) }>

                                                <Dropdown.Menu>
                                                    { _.sortBy( getActiveProjects(), item => item.project.name ).map( ( { projectId, project } ) => (
                                                        <Dropdown.Item key={ projectId } onClick={ () => handleChangeProjectContext( projectId ) }>
                                                            <span className={ classNames( 'text-capitalize', getSubmenuLinkClassesForPath( RouterUtility.placeParamsInPath( routePaths.projectBase, { projectId } ), ) ) }>{ project.name }</span>
                                                        </Dropdown.Item>
                                                    ) ) }
                                                </Dropdown.Menu>

                                            </Dropdown>

                                        </StyledContainer>

                                    </Menu.Item>

                                ) }

                                { /* Version Selection */ }
                                { (
                                  !noProjectContext &&
                                  dropdownProjectId &&
                                  getSelectedProjectInfo( dropdownProjectId )?.projectVersions ) && (

                                      <Menu.Item className={ 'no-padding' }>

                                          <StyledContainer bgColor={ dropdownProjectId ? CorvalUIColor.LIGHTBLUE : null }
                                                           borderSize={ dropdownProjectId ? Size.SM : null }
                                                           borderColor={ dropdownProjectId ? CorvalUIColor.DARKBLUE : null }
                                                           className={ 'top-nav-project-select padding-xs padding-sm-left margin-xs-left' }
                                                           style={ { borderRadius: 50, marginRight: '1rem' } }>


                                              <Dropdown item
                                                        scrolling={ true }
                                                        className={ classNames( 'flexbox--justify-between' ) }
                                                        style={ { maxWidth: 200 } }
                                                        trigger={ (
                                                            <div className="text text--no-wrap text-capitalize" role="alert" aria-live="polite" aria-atomic="true">
                                                                { _.findWhere( getSelectedProjectInfo( dropdownProjectId )?.projectVersions, { id: projectVersionId } )?.name ?? 'Select Version' }
                                                            </div>
                                                        ) }>

                                                  <Dropdown.Menu>
                                                      { getSelectedProjectInfo( dropdownProjectId ).projectVersions.map( ( { id, name, isDefaultVersion, isActiveVersion, description } ) => {

                                                          const isActive         = ( id === projectVersionId ),
                                                                referenceIdLabel = _.last( id.split( '-' ) ),
                                                                hasDesc          = !_.isEmpty( description?.trim() );

                                                          return (
                                                              <Dropdown.Item key={ id }
                                                                             style={ { minWidth: 300 } }
                                                                             onClick={ () => handleChangeProjectVersion( id ) }>

                                                                  <Grid columns={ `16px 1fr auto` }
                                                                        rows={ `auto auto` }
                                                                        areas={ [
                                                                            'activeIndicator title title',
                                                                            '. desc desc',
                                                                            '. versionNumber defaultIndicator',
                                                                        ] } rowGap={ '6px' }>

                                                                      <Cell area={ 'title' }>
                                                                        <span className={ classNames( {
                                                                            'text-capitalize':   true,
                                                                            'text--colored-red': isActive,
                                                                            'text--bold':        isActive,
                                                                        } ) }>{ name }</span>
                                                                      </Cell>

                                                                      <Cell area={ 'activeIndicator' } center>

                                                                          { isActiveVersion
                                                                            ? <ActiveProjectVersionIndicator isActive={ isActiveVersion }/>
                                                                            : isActive
                                                                              ? (
                                                                                  <Popup trigger={ <Icon className={ 'cursor--pointer opacity-hover--85' } name={ 'chevron right' } color={ 'red' }/> }>
                                                                                      <FinePrintMessage>Currently loaded version</FinePrintMessage>
                                                                                  </Popup>
                                                                              ) : null
                                                                          }

                                                                      </Cell>

                                                                      <Cell area={ 'versionNumber' }>

                                                                          <span className={ 'font--mono text--xs cursor--pointer opacity-hover--80, text--colored-grey' }>{ referenceIdLabel }</span>

                                                                      </Cell>

                                                                      { hasDesc && (
                                                                          <Cell area={ 'desc' }>
                                                                              <FinePrintMessage className={ 'text--wrap' }>{ description }</FinePrintMessage>
                                                                          </Cell>
                                                                      ) }

                                                                      { isDefaultVersion && (
                                                                          <Cell area={ 'defaultIndicator' } className={ 'text-right' }>

                                                                              <DefaultProjectVersionLabel popupProps={ { position: 'top right' } }/>

                                                                          </Cell>
                                                                      ) }
                                                                  </Grid>

                                                              </Dropdown.Item>
                                                          );
                                                      } ) }
                                                  </Dropdown.Menu>

                                              </Dropdown>

                                          </StyledContainer>

                                      </Menu.Item>

                                  ) }

                            </Menu>

                        </Cell>
                        <Cell>

                            {/* Project Menu */ }
                            <StyledContainer position={ Position.RELATIVE } style={ { zIndex: 0, left: -10 } }>

                                { hasProjects() && projectNavObjects && (

                                    <GenericNav navObjects={ projectNavObjects }>

                                        { ( navItems: GenericNavComponentItem[] ) => {

                                            // get top level active index
                                            let activeIndex = _.findIndex( navItems, { isActive: true } ) ?? null;

                                            return (

                                                <Menu { ...menuProps } size={ 'small' } activeIndex={ activeIndex } className={ classNames( menuClasses, 'fade-in' ) } style={ { zIndex: 1 } }>

                                                    <Menu.Item>
                                                        <Link className={ getMenuLinkClassesForPath( routePaths.dashboard ) } to={ routePaths.dashboard }>Home</Link>
                                                    </Menu.Item>

                                                    {featureFlags.enableAiAssistant && (
                                                        <Menu.Item className={ classNames( getMenuLinkClassesForPath( routePaths.doraAi ), 'position--relative' ) }>
                                                            <Link to={ routePaths.doraAi }>AI Assistant</Link>
                                                            { pathIsActive( routePaths.doraAi ) && <MainNavActiveIndicator/> }
                                                        </Menu.Item>
                                                    ) }

                                                    <Menu.Item className={ classNames( getMenuLinkClassesForPath( routePaths.consultantDash ), 'position--relative' ) }>
                                                        <Link to={ routePaths.consultantDash }>Consultant Dash</Link>
                                                        { pathIsActive( routePaths.consultantDash ) && <MainNavActiveIndicator/> }
                                                    </Menu.Item>

                                                    {/* Iterate over project specific menu items */ }
                                                    { navItems.map( ( item, index ) => {

                                                        let { isActive, route, path }        = item,
                                                            { label, help = null, subItems } = item,
                                                            hasSubNavItems                   = ( _.isArray( subItems ) && ( subItems.length > 0 ) ),
                                                            hasActiveSubItems                = _.some( subItems, ( { path, params } ) => path ? pathIsActive( RouterUtility.getRoute( path, params ) ) : false );

                                                        return (

                                                            <React.Fragment key={ index }>


                                                                { hasSubNavItems ? (

                                                                    <Dropdown item
                                                                              key={ `${ route }-${ label }` }
                                                                              icon={ null }
                                                                              closeOnEscape={ true }
                                                                              closeOnBlur={ true }
                                                                              openOnFocus={ true }
                                                                              closeOnChange={ true }
                                                                              compact={ true }
                                                                              pointing={ 'top left' }
                                                                              className={ classNames( getMenuLinkClassesForPath( route ), 'padding-xs-vertical padding-sm-horizontal position--relative' ) }
                                                                              style={ { paddingLeft: '1em', paddingRight: '1em' } }
                                                                              trigger={ (

                                                                                  <>
                                                                                      { label }

                                                                                      { ( isActive || ( hasSubNavItems && hasActiveSubItems ) ) && <MainNavActiveIndicator/> }
                                                                                  </>

                                                                              ) }>

                                                                        <Dropdown.Menu style={ { width: 'max-content' } }>
                                                                            { subItems.map( ( subItem: GenericNavItem ) => {

                                                                                let menuLinkPath = subItem.path ? RouterUtility.getRoute( subItem.path, subItem.params ) : null;

                                                                                return (

                                                                                    <Dropdown.Item key={ subItem.path } disabled={ !dropdownProjectId }>

                                                                                        <Link to={ menuLinkPath }
                                                                                              className={ getSubmenuLinkClassesForPath( menuLinkPath ) }
                                                                                              onMouseUp={ () => handleChangeProjectSection( subItem.path ) }>

                                                                                            <Grid columns={ `minmax(25px, auto) 1fr` } columnGap={ '0px' }>
                                                                                                <Cell center><Icon fitted { ...subItem.icon } /></Cell>
                                                                                                <Cell>{ subItem.label }</Cell>
                                                                                            </Grid>

                                                                                        </Link>

                                                                                    </Dropdown.Item>
                                                                                );
                                                                            } ) }

                                                                        </Dropdown.Menu>

                                                                    </Dropdown>

                                                                ) : (
                                                                      <Menu.Item key={ `${ path }-${ label }` }
                                                                                 disabled={ !dropdownProjectId }
                                                                                 className={ classNames( getMenuLinkClassesForPath( route ), 'position--relative' ) }
                                                                                 style={ { paddingLeft: '1em', paddingRight: '1em' } }>

                                                                          { route
                                                                            ? <Link to={ route } onMouseUp={ () => handleChangeProjectSection( path ) }>{ label }</Link>
                                                                            : <>{ label }</>
                                                                          }

                                                                          { isActive && <MainNavActiveIndicator/> }

                                                                      </Menu.Item>

                                                                  ) }
                                                            </React.Fragment>

                                                        );
                                                    } ) }

                                                </Menu>

                                            );
                                        } }
                                    </GenericNav>
                                ) }
                            </StyledContainer>

                        </Cell>
                    </Grid>

                </StyledContainer>
            </Cell>

        </Grid>
    );
};

const MainNavActiveIndicator = () => (
    <StyledContainer
        bgColor={ SemanticUIColor.RED }
        position={ Position.ABSOLUTE } left right
        style={ { bottom: -12, height: 3 } }
    />
);

NavComponent.defaultProps = defaultProps;

export { NavComponent as MainNav };
